/**
 * Query key factories for TanStack Query.
 * Shape: ['domain', 'resource', ...filters]
 * Always include clinicScope when the resource is clinic-scoped.
 */

export const queryKeys = {
  expense: {
    all: ['expense'] as const,
    categories: (filters?: {
      includeInactive?: boolean;
      search?: string;
      page?: number;
      limit?: number;
    }) => ['expense', 'categories', filters ?? {}] as const,
    list: (filters: object) =>
      ['expense', 'list', filters] as const,
  },
  income: {
    all: ['income'] as const,
    categories: (filters?: {
      includeInactive?: boolean;
      search?: string;
      page?: number;
      limit?: number;
    }) => ['income', 'categories', filters ?? {}] as const,
    list: (filters: object) =>
      ['income', 'list', filters] as const,
  },
  appointment: {
    all: ['appointment'] as const,
    types: (filters?: object) =>
      ['appointment', 'types', filters ?? {}] as const,
    list: (filters: object) =>
      ['appointment', 'list', filters] as const,
    detail: (id: string) => ['appointment', 'detail', id] as const,
    visitKindSuggestion: (filters: {
      patientId?: string;
      leadId?: string;
      excludeAppointmentId?: string;
    }) => ['appointment', 'visit-kind-suggestion', filters] as const,
    calendar: (filters: object) =>
      ['appointment', 'calendar', filters] as const,
    kanban: (filters: object) =>
      ['appointment', 'kanban', filters] as const,
  },
  savedView: {
    all: ['savedView'] as const,
    list: (resourceKey: string) => ['savedView', 'list', resourceKey] as const,
  },
  listLayout: {
    all: ['listLayout'] as const,
    byResource: (resourceKey: string) => ['listLayout', resourceKey] as const,
  },
  dashboardLayout: {
    all: ['dashboardLayout'] as const,
  },
  navWorkspace: {
    all: ['navWorkspace'] as const,
  },
  scheduling: {
    all: ['scheduling'] as const,
    assignments: (filters: object) =>
      ['scheduling', 'assignments', filters] as const,
    weeklyTemplates: (filters?: object) =>
      ['scheduling', 'weekly-templates', filters ?? {}] as const,
    weeklyTemplate: (id: string) =>
      ['scheduling', 'weekly-template', id] as const,
    shiftTypes: (filters?: object) =>
      ['scheduling', 'shift-types', filters ?? {}] as const,
    timeOffTypes: (filters?: object) =>
      ['scheduling', 'time-off-types', filters ?? {}] as const,
    timeOff: (filters: object) =>
      ['scheduling', 'time-off', filters] as const,
    timeOffDetail: (id: string) => ['scheduling', 'time-off', id] as const,
    timeOffDayWindows: (staffId: string, date: string) =>
      ['scheduling', 'time-off', 'day-windows', staffId, date] as const,
    leaveStaffRefs: ['scheduling', 'time-off', 'leave-staff-refs'] as const,
    shifts: (filters: object) =>
      ['scheduling', 'shifts', filters] as const,
    shiftDetail: (id: string) => ['scheduling', 'shift', id] as const,
    leaveEntitlements: (filters: object) =>
      ['scheduling', 'leave-entitlements', filters] as const,
    leaveBalance: (filters: object) =>
      ['scheduling', 'leave-balance', filters] as const,
    leaveLedger: (filters: object) =>
      ['scheduling', 'leave-ledger', filters] as const,
    leaveReport: (filters: object) =>
      ['scheduling', 'leave-report', filters] as const,
    calendar: (filters: object) =>
      ['scheduling', 'calendar', filters] as const,
    busyTimes: (filters: object) =>
      ['scheduling', 'busy-times', filters] as const,
    staff: (filters?: object) =>
      ['scheduling', 'staff', filters ?? {}] as const,
    availability: (filters: object) =>
      ['scheduling', 'availability', filters] as const,
    calendarSlots: (filters: object) =>
      ['scheduling', 'calendar-slots', filters] as const,
  },
  labo: {
    all: ['labo'] as const,
    partners: (filters?: object) =>
      ['labo', 'partners', filters ?? {}] as const,
    partner: (id: string) => ['labo', 'partner', id] as const,
    partnerStats: (id: string) => ['labo', 'partner-stats', id] as const,
    activePartners: ['labo', 'partners', 'active'] as const,
    workTypes: (filters?: object) =>
      ['labo', 'work-types', filters ?? {}] as const,
    materials: (filters?: object) =>
      ['labo', 'materials', filters ?? {}] as const,
    shades: (filters?: object) =>
      ['labo', 'shades', filters ?? {}] as const,
    orders: (filters: object) =>
      ['labo', 'orders', filters] as const,
    order: (id: string) => ['labo', 'order', id] as const,
    report: (reportKey: string, params: object) =>
      ['labo', 'report', reportKey, params] as const,
  },
  payment: {
    all: ['payment'] as const,
    list: (filters: object) =>
      ['payment', 'list', filters] as const,
    detail: (id: string) => ['payment', 'detail', id] as const,
    planAdvanceRefundPreview: (planId: string, amount: number) =>
      ['payment', 'plan-advance-refund-preview', planId, amount] as const,
    methods: (filters?: object) =>
      ['payment', 'methods', filters ?? {}] as const,
  },
  promotion: {
    all: ['promotion'] as const,
    list: (filters?: object) =>
      ['promotion', 'list', filters ?? {}] as const,
    detail: (id: string) => ['promotion', 'detail', id] as const,
  },
  automation: {
    all: ['automation'] as const,
    rules: (filters: object) =>
      ['automation', 'rules', filters] as const,
    runs: (filters: object) =>
      ['automation', 'runs', filters] as const,
    ruleOptions: ['automation', 'rule-options'] as const,
  },
  commission: {
    all: ['commission'] as const,
    rules: (filters?: object) =>
      ['commission', 'rules', filters ?? {}] as const,
    rule: (id: string) => ['commission', 'rule', id] as const,
    entries: (filters?: object) =>
      ['commission', 'entries', filters ?? {}] as const,
    staffEntries: (staffId: string, filters: object) =>
      ['commission', 'staffEntries', staffId, filters] as const,
    summary: (filters?: object) =>
      ['commission', 'summary', filters ?? {}] as const,
    periodPreview: (filters?: object) =>
      ['commission', 'periodPreview', filters ?? {}] as const,
    periods: (filters?: object) =>
      ['commission', 'periods', filters ?? {}] as const,
  },
  wallet: {
    all: ['wallet'] as const,
    list: (filters?: object) =>
      ['wallet', 'list', filters ?? {}] as const,
    detail: (id: string) => ['wallet', 'detail', id] as const,
    summary: (patientId: string) => ['wallet', 'summary', patientId] as const,
    balance: (patientId: string) => ['wallet', 'balance', patientId] as const,
    transactions: (patientId: string, filters?: object) =>
      ['wallet', 'transactions', patientId, filters ?? {}] as const,
    topups: (patientId: string, filters?: object) =>
      ['wallet', 'topups', patientId, filters ?? {}] as const,
    refunds: (patientId: string, filters?: object) =>
      ['wallet', 'refunds', patientId, filters ?? {}] as const,
  },
  finance: {
    all: ['finance'] as const,
    cashbook: (filters: object) =>
      ['finance', 'cashbook', filters] as const,
    summary: (filters: object) =>
      ['finance', 'summary', filters] as const,
  },
  prescription: {
    all: ['prescription'] as const,
    list: (filters: object) =>
      ['prescription', 'list', filters] as const,
    detail: (id: string) => ['prescription', 'detail', id] as const,
    drugs: (filters?: object) =>
      ['prescription', 'drugs', filters ?? {}] as const,
    drug: (id: string) => ['prescription', 'drug', id] as const,
    templates: (filters?: object) =>
      ['prescription', 'templates', filters ?? {}] as const,
    template: (id: string) => ['prescription', 'template', id] as const,
  },
  treatmentCatalog: {
    all: ['treatment-catalog'] as const,
    list: (filters: object) =>
      ['treatment-catalog', 'list', filters] as const,
    detail: (id: string) => ['treatment-catalog', 'detail', id] as const,
    uoms: (filters?: object) =>
      ['treatment-catalog', 'uoms', filters ?? {}] as const,
    categories: (filters?: object) =>
      ['treatment-catalog', 'categories', filters ?? {}] as const,
  },
  treatmentJournal: {
    all: ['treatment-journal'] as const,
    list: (filters?: object) =>
      ['treatment-journal', 'list', filters ?? {}] as const,
  },
  treatmentPlan: {
    all: ['treatment-plan'] as const,
    list: (filters: object) =>
      ['treatment-plan', 'list', filters] as const,
    detail: (id: string) => ['treatment-plan', 'detail', id] as const,
    promotionPreview: (params: object | null) =>
      ['treatment-plan', 'promotion-preview', params] as const,
  },
  treatmentPlanItem: {
    all: ['treatment-plan-item'] as const,
    list: (filters: object) =>
      ['treatment-plan-item', 'list', filters] as const,
    detail: (id: string) => ['treatment-plan-item', 'detail', id] as const,
  },
  treatmentSession: {
    all: ['treatment-session'] as const,
    list: (filters: object) =>
      ['treatment-session', 'list', filters] as const,
    detail: (id: string) => ['treatment-session', 'detail', id] as const,
  },
  task: {
    all: ['task'] as const,
    list: (filters: object) =>
      ['task', 'list', filters] as const,
    detail: (id: string) => ['task', 'detail', id] as const,
    relatedSearch: (type: string, query: string) =>
      ['task', 'related-search', type, query] as const,
  },
  project: {
    all: ['project'] as const,
    list: (filters: object) =>
      ['project', 'list', filters] as const,
    detail: (id: string) => ['project', 'detail', id] as const,
    favorites: ['project', 'favorites'] as const,
  },
  kpi: {
    all: ['kpi'] as const,
    metrics: (filters?: object) =>
      ['kpi', 'metrics', filters ?? {}] as const,
    templates: (filters?: object) =>
      ['kpi', 'templates', filters ?? {}] as const,
    template: (id: string) => ['kpi', 'template', id] as const,
    cycles: (filters?: object) =>
      ['kpi', 'cycles', filters ?? {}] as const,
    cycle: (id: string) => ['kpi', 'cycle', id] as const,
    sheets: (filters?: object) =>
      ['kpi', 'sheets', filters ?? {}] as const,
    sheet: (id: string) => ['kpi', 'sheet', id] as const,
    me: (period?: string) => ['kpi', 'me', period ?? ''] as const,
    staffOptions: () => ['kpi', 'staff-options'] as const,
  },
  lead: {
    all: ['lead'] as const,
    list: (filters: object) =>
      ['lead', 'list', filters] as const,
    detail: (id: string) => ['lead', 'detail', id] as const,
  },
  messaging: {
    all: ['messaging'] as const,
    capabilities: ['messaging', 'capabilities'] as const,
    pages: ['messaging', 'pages'] as const,
    conversations: (filters: object) =>
      ['messaging', 'conversations', filters] as const,
    conversation: (id: string) => ['messaging', 'conversation', id] as const,
    messages: (id: string, filters?: object) =>
      (filters
        ? (['messaging', 'messages', id, filters] as const)
        : (['messaging', 'messages', id] as const)),
    historySync: (id: string, filters?: object) =>
      (filters
        ? (['messaging', 'history-sync', id, filters] as const)
        : (['messaging', 'history-sync', id] as const)),
    cannedResponses: (filters?: { includeInactive?: boolean }) =>
      ['messaging', 'canned-responses', filters ?? {}] as const,
  },
  internalChat: {
    all: ['internalChat'] as const,
    threads: (filters?: object) =>
      ['internalChat', 'threads', filters ?? {}] as const,
    thread: (id: string) => ['internalChat', 'thread', id] as const,
    messages: (id: string, filters?: object) =>
      (filters
        ? (['internalChat', 'messages', id, filters] as const)
        : (['internalChat', 'messages', id] as const)),
    recipients: (filters?: object) =>
      ['internalChat', 'recipients', filters ?? {}] as const,
    attachments: (id: string) => ['internalChat', 'attachments', id] as const,
    typing: (id: string) => ['internalChat', 'typing', id] as const,
    messageEdits: (threadId: string, messageId: string) =>
      ['internalChat', 'messageEdits', threadId, messageId] as const,
  },
  overtime: {
    all: ['overtime'] as const,
    types: (filters?: object) =>
      ['overtime', 'types', filters ?? {}] as const,
    type: (id: string) => ['overtime', 'type', id] as const,
    requests: (filters: object) =>
      ['overtime', 'requests', filters] as const,
    request: (id: string) => ['overtime', 'request', id] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    list: (filters: object) =>
      ['attendance', 'list', filters] as const,
    detail: (id: string) => ['attendance', 'detail', id] as const,
    requests: (filters: object) =>
      ['attendance', 'requests', filters] as const,
    request: (id: string) => ['attendance', 'request', id] as const,
    monthlySheet: (filters: object) =>
      ['attendance', 'monthlySheet', filters] as const,
    summarySheet: (filters: object) =>
      ['attendance', 'summarySheet', filters] as const,
    timesheet: (filters: object) =>
      ['attendance', 'timesheet', filters] as const,
    geofence: (filters?: object) =>
      ['attendance', 'geofence', filters ?? {}] as const,
  },
  workTimesheet: {
    all: ['workTimesheet'] as const,
    sheet: (filters: object) =>
      ['workTimesheet', 'sheet', filters] as const,
    journal: (filters: object) =>
      ['workTimesheet', 'journal', filters] as const,
  },
  audit: {
    all: ['audit'] as const,
    list: (filters: object) =>
      ['audit', 'list', filters] as const,
    detail: (id: string) => ['audit', 'detail', id] as const,
    byEntity: (entityType: string, entityId: string, filters?: object) =>
      filters === undefined
        ? (['audit', 'entity', entityType, entityId] as const)
        : (['audit', 'entity', entityType, entityId, filters] as const),
  },
  comment: {
    all: ['comment'] as const,
    bySubject: (
      subjectType: string,
      subjectId: string,
      filters?: object
    ) => ['comment', 'subject', subjectType, subjectId, filters ?? {}] as const,
    mentionCandidates: (filters?: object) =>
      ['comment', 'mentionCandidates', filters ?? {}] as const,
  },
  notification: {
    all: ['notification'] as const,
    list: (filters?: object) =>
      ['notification', 'list', filters ?? {}] as const,
    unread: ['notification', 'unread'] as const,
  },
  patient: {
    all: ['patient'] as const,
    customerSources: (filters?: { includeInactive?: boolean }) =>
      ['patient', 'customer-sources', filters ?? {}] as const,
    list: (filters: object) =>
      ['patient', 'list', filters] as const,
    detail: (id: string) => ['patient', 'detail', id] as const,
    documents: (patientId: string) => ['patient', 'documents', patientId] as const,
    communications: (patientId: string) => ['patient', 'communications', patientId] as const,
    messageDeliveries: (patientId: string, params?: object) =>
      ['patient', 'message-deliveries', patientId, params ?? {}] as const,
    prescriptions: (patientId: string) => ['patient', 'prescriptions', patientId] as const,
  },
  clinical: {
    all: ['clinical'] as const,
    chart: (patientId: string) => ['clinical', 'chart', patientId] as const,
  },
  diagnosis: {
    all: ['diagnosis'] as const,
    codes: (filters?: object) =>
      ['diagnosis', 'codes', filters ?? {}] as const,
    catalogSummary: (filters?: object) =>
      ['diagnosis', 'catalog-summary', filters ?? {}] as const,
    blocks: (filters?: { includeInactive?: boolean; system?: string }) =>
      ['diagnosis', 'blocks', filters ?? {}] as const,
  },
  staff: {
    all: ['staff'] as const,
    list: (filters: object) =>
      ['staff', 'list', filters] as const,
    /** Active picker catalog for one clinic scope (`all` or a clinic id). */
    roster: (clinicScope: string) => ['staff', 'roster', clinicScope] as const,
    detail: (id: string) => ['staff', 'detail', id] as const,
    departments: (filters?: {
      includeInactive?: boolean;
      search?: string;
      status?: string;
      page?: number;
      limit?: number;
      sort?: string;
    }) => ['staff', 'departments', filters ?? {}] as const,
  },
  payroll: {
    all: ['payroll'] as const,
    compensation: (staffId: string) => ['payroll', 'compensation', staffId] as const,
    assignments: ['payroll', 'compensation', 'assignments'] as const,
    components: (includeInactive: boolean) =>
      ['payroll', 'components', includeInactive] as const,
    periods: (filters: object) => ['payroll', 'periods', filters] as const,
    period: (id: string) => ['payroll', 'period', id] as const,
    entries: (periodId: string) => ['payroll', 'entries', periodId] as const,
    entry: (id: string) => ['payroll', 'entry', id] as const,
    entryKpiLines: (id: string) => ['payroll', 'entry', id, 'kpi-lines'] as const,
    entryCommissionLines: (id: string) => ['payroll', 'entry', id, 'commission-lines'] as const,
    entryReferralLines: (id: string) =>
      ['payroll', 'entry', id, 'referral-commission-lines'] as const,
    mePayslips: (filters: object) =>
      ['payroll', 'mePayslips', filters] as const,
    staffPayslips: (staffId: string, filters: object) =>
      ['payroll', 'staffPayslips', staffId, filters] as const,
    advances: (filters: object) =>
      ['payroll', 'advances', filters] as const,
    meAdvances: (filters: object) =>
      ['payroll', 'meAdvances', filters] as const,
    advanceStaffRefs: ['payroll', 'advanceStaffRefs'] as const,
  },
  clinic: {
    all: ['clinic'] as const,
    list: (filters?: object) =>
      ['clinic', 'list', filters ?? {}] as const,
    detail: (id: string) => ['clinic', 'detail', id] as const,
    clientIp: ['clinic', 'clientIp'] as const,
    chairs: (clinicId: string, filters?: object) =>
      ['clinic', 'chairs', clinicId, filters ?? {}] as const,
  },
  identity: {
    all: ['identity'] as const,
    organization: ['identity', 'organization'] as const,
    me: ['identity', 'me'] as const,
    sessions: ['identity', 'sessions'] as const,
    users: (filters: object) =>
      ['identity', 'users', filters] as const,
    user: (id: string) => ['identity', 'user', id] as const,
    permissionGroups: (filters?: object) =>
      ['identity', 'permission-groups', filters ?? {}] as const,
  },
  settings: {
    all: ['settings'] as const,
    organization: ['settings', 'organization'] as const,
    documentNumbering: ['settings', 'documentNumbering'] as const,
    emailIntegration: ['settings', 'integrations', 'email'] as const,
    messagingIntegration: ['settings', 'integrations', 'messaging'] as const,
    messagingSyncStatus: ['settings', 'integrations', 'messaging', 'sync-status'] as const,
    emailDeliveries: (filters?: object) =>
      ['settings', 'integrations', 'email', 'deliveries', filters ?? {}] as const,
    voiceCloudIntegration: ['settings', 'integrations', 'voicecloud'] as const,
    voiceCloudBalance: ['settings', 'integrations', 'voicecloud', 'balance'] as const,
    voiceCloudDeliveriesRoot: ['settings', 'integrations', 'voicecloud', 'deliveries'] as const,
    voiceCloudDeliveries: (filters?: object) =>
      ['settings', 'integrations', 'voicecloud', 'deliveries', filters ?? {}] as const,
    voiceCloudTemplates: ['settings', 'integrations', 'voicecloud', 'templates'] as const,
    voiceCloudSendTemplatesRoot: ['settings', 'integrations', 'voicecloud', 'send-templates'] as const,
    voiceCloudSendTemplates: (filters?: object) =>
      ['settings', 'integrations', 'voicecloud', 'send-templates', filters ?? {}] as const,
    omicallIntegration: ['settings', 'integrations', 'omicall'] as const,
    omicallExtensions: ['settings', 'integrations', 'omicall', 'extensions'] as const,
  },
  calls: {
    all: ['calls'] as const,
    meSip: ['calls', 'me', 'sip'] as const,
    contacts: (filters: object) => ['calls', 'contacts', filters] as const,
    contact: (id: string) => ['calls', 'contact', id] as const,
  },
  platform: {
    all: ['platform'] as const,
    organizations: (filters?: object) =>
      ['platform', 'organizations', filters ?? {}] as const,
  },
  me: {
    all: ['me'] as const,
    staff: ['me', 'staff'] as const,
    timeOff: (filters?: object) =>
      ['me', 'time-off', filters ?? {}] as const,
    attendance: (filters?: object) =>
      ['me', 'attendance', filters ?? {}] as const,
    schedule: (filters: object) =>
      ['me', 'schedule', filters] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    list: (filters: object) =>
      ['inventory', 'list', filters] as const,
    detail: (id: string) => ['inventory', 'detail', id] as const,
    movements: (filters: object) =>
      ['inventory', 'movements', filters] as const,
    stockIn: (filters: object) =>
      ['inventory', 'stock-in', filters] as const,
    stockInDetail: (id: string) => ['inventory', 'stock-in', 'detail', id] as const,
    stockOut: (filters: object) =>
      ['inventory', 'stock-out', filters] as const,
    stockOutDetail: (id: string) => ['inventory', 'stock-out', 'detail', id] as const,
    transfer: (filters: object) =>
      ['inventory', 'transfer', filters] as const,
    transferDetail: (id: string) => ['inventory', 'transfer', 'detail', id] as const,
    stocktake: (filters: object) =>
      ['inventory', 'stocktake', filters] as const,
    stocktakeDetail: (id: string) => ['inventory', 'stocktake', 'detail', id] as const,
    reportBalance: (filters: object) =>
      ['inventory', 'reports', 'balance', filters] as const,
    reportMovement: (filters: object) =>
      ['inventory', 'reports', 'movement', filters] as const,
    reportLowStock: (filters: object) =>
      ['inventory', 'reports', 'low-stock', filters] as const,
    suppliers: (filters?: object) =>
      ['inventory', 'suppliers', filters ?? {}] as const,
    taxonomy: (filters: { kind: string; includeInactive?: boolean }) =>
      ['inventory', 'taxonomy', filters] as const,
    warehouses: (filters?: object) =>
      ['inventory', 'warehouses', filters ?? {}] as const,
    lots: (itemId: string, clinicId: string) =>
      ['inventory', 'lots', itemId, clinicId] as const,
    serials: (
      itemId: string,
      clinicId: string,
      filters: object = {}
    ) => ['inventory', 'serials', itemId, clinicId, filters] as const,
  },
  referrers: {
    all: ['referrers'] as const,
    list: (filters: object) => ['referrers', 'list', filters] as const,
    candidates: (filters: object) => ['referrers', 'candidates', filters] as const,
  },
  referralCommissions: {
    all: ['referral-commissions'] as const,
    rules: (filters: object = {}) => ['referral-commissions', 'rules', filters] as const,
    entries: (filters: object) => ['referral-commissions', 'entries', filters] as const,
    preview: (filters: object) => ['referral-commissions', 'preview', filters] as const,
    summary: (filters: object) => ['referral-commissions', 'summary', filters] as const,
  },
  reports: {
    all: ['reports'] as const,
    dashboard: (filters?: object) =>
      ['reports', 'dashboard', filters ?? {}] as const,
    data: (filters: object) =>
      ['reports', 'data', filters] as const,
    appointmentSummary: (filters: object) =>
      ['reports', 'appointment-summary', filters] as const,
    referrers: (filters: object) =>
      ['reports', 'referrers', filters] as const,
    referrerList: (filters: object) =>
      ['reports', 'referrer-list', filters] as const,
    overview: (filters: object) =>
      ['reports', 'overview', filters] as const,
    revenue: (filters: object) =>
      ['reports', 'revenue', filters] as const,
    patients: (filters: object) =>
      ['reports', 'patients', filters] as const,
    treatments: (filters: object) =>
      ['reports', 'treatments', filters] as const,
  },
} as const;
